import { useState, useEffect } from 'react'
import { doc, onSnapshot, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Booking } from '@/types/booking'

export function useRealtimeBooking(bookingId: string | null | undefined) {
  const [booking, setBooking] = useState<Booking | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!bookingId) {
      setBooking(null)
      return
    }

    // Set up real-time listener
    const bookingRef = doc(db, 'bookings', bookingId)
    
    const unsubscribe = onSnapshot(
      bookingRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          const bookingData: Booking = {
            id: snapshot.id,
            chatId: data.chatId,
            userId: data.userId,
            artistId: data.artistId,
            clientUsername: data.clientUsername,
            type: data.type,
            shortSummary: data.shortSummary || '',
            flashId: data.flashId,
            flashImageUrl: data.flashImageUrl,
            customImageUrl: data.customImageUrl,
            customDesignStatus: data.customDesignStatus,
            scheduledTimestamp: data.scheduledTimestamp instanceof Timestamp ? data.scheduledTimestamp.toDate() : new Date(data.scheduledTimestamp),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
            estimatedTime: data.estimatedTime,
            googleCalendarEventId: data.googleCalendarEventId,
            status: data.status,
            bookingCompleteForm: data.bookingCompleteForm ? {
              completedAt: data.bookingCompleteForm.completedAt instanceof Timestamp 
                ? data.bookingCompleteForm.completedAt.toDate() 
                : new Date(data.bookingCompleteForm.completedAt),
              finalPrice: data.bookingCompleteForm.finalPrice,
              actualDate: data.bookingCompleteForm.actualDate instanceof Timestamp
                ? data.bookingCompleteForm.actualDate.toDate()
                : data.bookingCompleteForm.actualDate ? new Date(data.bookingCompleteForm.actualDate) : undefined,
              notes: data.bookingCompleteForm.notes
            } : undefined
          }
          setBooking(bookingData)
          setError(null)
        } else {
          setBooking(null)
        }
      },
      (err) => {
        console.error('Error setting up real-time listener for booking:', err)
        setError('Failed to load booking information')
      }
    )

    // Clean up listener on unmount or when bookingId changes
    return () => {
      unsubscribe()
    }
  }, [bookingId])

  return { booking, error }
}

